import {BehaviorSubject} from 'rxjs'
import _ from 'lodash'
import {DeepPartial} from 'seamless-immutable'
import {useState, useEffect, useRef} from 'react'

export class Published<T> {
  public get value(): T {
    return this.subject.value
  }

  public get isLoading(): boolean {
    return this._isLoading?.value ?? false
  }

  private _isLoading?: Published<boolean>
  private subject: BehaviorSubject<T>

  public constructor(value: T) {
    this.subject = new BehaviorSubject<T>(value)
  }

  public withLoader(loading: boolean = false): Published<T> {
    this._isLoading = new Published<boolean>(loading)
    return this
  }

  public publish = (value: T) => {
    this.subject.next(value)
  }

  public startLoading = () => {
    this._isLoading?.publish(true)
  }

  public endLoading = () => {
    this._isLoading?.publish(false)
  }

  public reduce(reducer: (current: T) => T) {
    this.publish(reducer(this.value))
  }

  public update(partial: DeepPartial<T>, deep = false) {
    this.reduce((current) =>
      _(current)
        // @ts-ignore
        .merge(partial, {deep})
        .value()
    )
  }

  public asObservable() {
    return this.subject.asObservable()
  }

  public hook(): [T, boolean] {
    const value = usePublished(this)
    const isLoading = this._isLoading ? usePublished(this._isLoading) : false
    return [value, isLoading]
  }

  public valueOf(): T {
    return this.value
  }
}

export default function published<T>(value: T): Published<T> {
  // @ts-ignore
  const pb = new Published<T>(value)
  return pb.withLoader()
}

export function usePublished<T>(pb: Published<T>): T {
  const [value, setValue] = useState({state: pb.value})
  const isMounted = useRef<boolean>(true)
  useEffect(() => {
    isMounted.current = true
    const subscription = pb.asObservable().subscribe((data) => {
      if (isMounted.current) {
        setValue({state: data})
      }
    })
    return () => {
      isMounted.current = false
      subscription.unsubscribe()
    }
  }, [pb, setValue])
  return value.state
}
